import { Tooltip, TooltipProps } from "@mui/material";
import React, { useEffect, useRef, useState } from "react";
import { detectOverflow, type Modifier } from "@popperjs/core";

interface CustomTooltipProps extends TooltipProps {
	autoTranslate?: "yes" | "no";
	showWhenOverflow?: boolean;
	open?: boolean;
}

const isElementOverflowing = (el: HTMLElement): boolean =>
	el.scrollWidth - el.clientWidth > 1 || el.scrollHeight - el.clientHeight > 1;

const findTruncatingElement = (root: HTMLElement): HTMLElement => {
	if (isElementOverflowing(root)) {
		return root;
	}
	const descendants = root.querySelectorAll<HTMLElement>("*");
	for (let i = 0; i < descendants.length; i += 1) {
		if (isElementOverflowing(descendants[i])) {
			return descendants[i];
		}
	}
	return root;
};

const isOverflowing = (root: HTMLElement | null): boolean => {
	if (!root) {
		return false;
	}
	return isElementOverflowing(findTruncatingElement(root));
};

const PADDING = 8;
const MAX_WIDTH = 320;

const maxSize: Modifier<"maxSize", Record<string, never>> = {
	name: "maxSize",
	enabled: true,
	phase: "main",
	requiresIfExists: ["offset", "preventOverflow", "flip"],
	fn({ state }) {
		const TOOLTIP_MARGIN = 14;
		const overflow = detectOverflow(state, { padding: PADDING + TOOLTIP_MARGIN });
		const { width, height } = state.rects.popper;
		const [basePlacement] = state.placement.split("-");
		const heightEdge = basePlacement === "top" ? "top" : "bottom";
		const widthEdge = basePlacement === "left" ? "left" : "right";
		state.modifiersData.maxSize = {
			height: height - overflow[heightEdge],
			width: width - overflow[widthEdge],
		};
	},
};

const applyMaxSize: Modifier<"applyMaxSize", Record<string, never>> = {
	name: "applyMaxSize",
	enabled: true,
	phase: "beforeWrite",
	requires: ["maxSize"],
	fn({ state }) {
		const { height, width } = state.modifiersData.maxSize;
		state.styles.popper = {
			...state.styles.popper,
			maxHeight: `${Math.max(height, 80)}px`,
			maxWidth: `${Math.min(Math.max(width, 120), MAX_WIDTH)}px`,
		};
	},
};

export default function CustomTooltip({
	autoTranslate = "yes",
	arrow = false,
	children,
	className = "",
	disableHoverListener,
	enterDelay = 100,
	enterNextDelay = 0,
	placement = "bottom",
	showWhenOverflow = false,
	title,
	open,
}: Readonly<CustomTooltipProps>) {
	const ref = useRef<HTMLElement | null>(null);
	const [overflowing, setOverflowing] = useState(false);
	useEffect(() => {
		if (!showWhenOverflow) {
			return undefined;
		}
		const root = ref.current;
		if (!root) {
			return undefined;
		}
		let raf = 0;
		const check = () => {
			cancelAnimationFrame(raf);
			raf = requestAnimationFrame(() => setOverflowing(isOverflowing(root)));
		};
		check();
		const observer = new ResizeObserver(check);
		observer.observe(root);
		const truncating = findTruncatingElement(root);
		if (truncating !== root) {
			observer.observe(truncating);
		}
		return () => {
			cancelAnimationFrame(raf);
			observer.disconnect();
		};
	}, [showWhenOverflow, title, children]);
	const effectiveTitle = showWhenOverflow && !overflowing ? "" : title;
	return (
		<Tooltip
			ref={ref}
			arrow={arrow}
			classes={{
				tooltip: `font-sm-regular ${className}`,
			}}
			disableHoverListener={disableHoverListener}
			enterDelay={enterDelay}
			enterNextDelay={enterNextDelay}
			slotProps={{
				popper: {
					modifiers: [{ name: "preventOverflow", options: { padding: PADDING } }, maxSize, applyMaxSize],
				},
				tooltip: {
					sx: {
						maxHeight: "inherit",
						maxWidth: "inherit",
						overflowY: "auto",
						overscrollBehavior: "contain",
					},
				},
			}}
			{...(open !== undefined ? { open } : {})}
			placement={placement}
			title={effectiveTitle}
			translate={autoTranslate}
		>
			{children}
		</Tooltip>
	);
}
